import {
  Card,
  CardContent,
  DialogContentText,
  Divider,
  IconButton,
  Switch,
  Typography
} from "@material-ui/core";
import { useTheme } from "@material-ui/core";
import { useMediaQuery } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import InfoIcon from "@material-ui/icons/Info";
import SettingsIcon from "@material-ui/icons/Settings";
import StarIcon from "@material-ui/icons/Star";
import { hasPermission } from "@saleor/auth/misc";
import ActionDialog from "@saleor/components/ActionDialog";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import FormSpacer from "@saleor/components/FormSpacer";
import Grids from "@saleor/components/Grid";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SingleAutocompleteSelectField, {
  SingleAutocompleteChoiceType
} from "@saleor/components/SingleAutocompleteSelectField";
import { ChangeEvent } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import { DEFAULT_AUTO_CANCEL_DAYS } from "@saleor/store/constants";
import { TenantInStoreCancelConfig } from "@saleor/store/types/InStoreCancelConfig";
import { FetchMoreProps } from "@saleor/types";
import { PermissionEnum, SmopUserTypeCode } from "@saleor/types/globalTypes";
import classNames from "classnames";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { SmopStatusEnum } from "../../../types/globalTypes";
import StoreBannerImage from "../StoreBannerImage";
import { StoreCreatePageFormData } from "../StoreCreatePage";
import StoreFormFeatureCheckbox from "../StoreFormFeatureCheckbox";
import StoreFormInfoTextTypography from "../StoreFormInfoTextTypography";

const useStyles = makeStyles(
  theme => ({
    cardContentDisabled: {
      opacity: "0.6",
      pointerEvents: "none"
    },
    disabledField: {
      pointerEvents: "none"
    },
    gridContainer: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    },
    informationIcon: {
      display: "inline-flex",
      verticalAlign: "top"
    },
    textfieldPadding: {
      rightPadding: 4
    }
  }),
  { name: "StoreEditForm" }
);

export interface StoreEditFormProps {
  /** Form actions */
  submit: any;
  onDelete: () => void;
  discardButton: any;
  setIsAutoCancelConfigModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

  /** Form state */
  data: StoreCreatePageFormData;
  change: any;
  hasChange: any;
  disable: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  hasManageTenantPermission: boolean;

  /** Errors */
  error: {
    acctName: null;
    acctNum: null;
    bankName: null;
    cluster: null;
    contractId: null;
    designation: null;
    financeName: null;
    financeEmailAddress: null;
    financeMobileNum: null;
    ipay88SubMid: null;
    mallBranch: null;
    authorizeEmailAddress: null;
    authorizeMobileNum: null;
    paymentFeeRate: null;
    rentalType: null;
    sellerBrand: null;
    signatoryName: null;
    location: null;
    subTenantType: null;
  };

  /** Display values */
  clusterDisplayValue: string;
  sellerDisplayValue: string;
  mallDisplayValue: string;
  paymentRateDisplay: string;
  rentTypeDisplay: string;
  subTenantTypeDisplay: string;

  /** Data */
  brands: SingleAutocompleteChoiceType[];
  clusters: SingleAutocompleteChoiceType[];
  malls: SingleAutocompleteChoiceType[];
  paymentRateChoices: SingleAutocompleteChoiceType[];
  rentTypeChoices: SingleAutocompleteChoiceType[];
  subTenantChoices: SingleAutocompleteChoiceType[];
  autoCancelConfig: TenantInStoreCancelConfig["tenantInStoreCancelConfig"];

  /** Fetch actions */
  fetchMalls: (query: string) => void;
  fetchBrands: (query: string) => void;
  fetchClusters: (query: string) => void;
  fetchMoreMalls?: FetchMoreProps;
  fetchMoreBrands?: FetchMoreProps;
  fetchMoreClusters?: FetchMoreProps;

  /** Event handlers */
  onImageUpload?: (file: File) => void;
  onClusterChange: (event: ChangeEvent) => void;
  onBrandChangeStoreType(event: ChangeEvent<any>): void;
  onBrandChange(event: ChangeEvent<any>): void;
  onMallChange(event: ChangeEvent<any>): void;
  onPaymentChange(event: ChangeEvent<any>): void;
  onRentalChange(event: ChangeEvent<any>): void;
  onSubTenantTypeChange(event: ChangeEvent<any>): void;
}

const StoreEditForm: React.FC<StoreEditFormProps> = props => {
  const {
    /** Form actions */
    submit,
    onDelete,
    discardButton,
    setIsAutoCancelConfigModalOpen,

    /** Form state */
    data: formData,
    change,
    hasChange,
    disable,
    saveButtonBarState,
    hasManageTenantPermission,

    /** Errors */
    error,

    /** Display values */
    clusterDisplayValue,
    sellerDisplayValue,
    mallDisplayValue,
    // paymentRateDisplay,
    rentTypeDisplay,
    subTenantTypeDisplay,

    /** Data */
    brands,
    clusters,
    malls,
    paymentRateChoices,
    rentTypeChoices,
    subTenantChoices,
    autoCancelConfig,

    /** Fetch actions */
    fetchMalls,
    fetchBrands,
    fetchClusters,
    fetchMoreMalls,
    fetchMoreBrands,
    fetchMoreClusters,

    /** Event handlers */
    onImageUpload,
    onClusterChange,
    onBrandChangeStoreType,
    onBrandChange,
    onMallChange,
    onPaymentChange,
    onRentalChange,
    onSubTenantTypeChange
  } = props;
  const classes = useStyles({});
  const intl = useIntl();
  const { user } = useUser();
  const notify = useNotifier();
  const theme = useTheme();
  const mediumUpwards = useMediaQuery(theme.breakpoints.up("md"));

  const [statusModal, setModalStatus] = useState(false);
  const [hasError, setHasError] = useState(false);

  const isFnB = formData.storeType === "FOOD_AND_BEVERAGES";

  const userType = user?.userType?.code || "";
  const isStoreAdmin = userType === SmopUserTypeCode.STORE_ADMIN;
  const isSellerMaster = userType === SmopUserTypeCode.SELLER_MASTER;
  const isSellerMasterOrStoreAdmin = isSellerMaster || isStoreAdmin;
  const isAcquisitionOmiAdmin = [
    SmopUserTypeCode.ACQUISITION,
    SmopUserTypeCode.OMI_ADMIN,
    ""
  ].includes(userType);

  const handleTime = (
    value: any,
    timerName,
    onChange: (event: ChangeEvent<any>) => void
  ) => {
    setHasError(false);
    const formDate = value.target.value;

    if (formData.promoRibbonStart && formData.promoRibbonEnd) {
      if (timerName === "promoRibbonStart") {
        if (Moment(formData.promoRibbonEnd).isBefore(Moment(formDate))) {
          notify({
            status: "error",
            text: intl.formatMessage(commonMessages.timeError)
          });
          setHasError(true);
        }
      }
      if (timerName === "promoRibbonEnd") {
        if (Moment(formData.promoRibbonStart).isAfter(Moment(formDate))) {
          notify({
            status: "error",
            text: intl.formatMessage(commonMessages.timeError)
          });
          setHasError(true);
        }
      }
    }
    if (formDate && !hasError) {
      onChange({
        target: {
          name: timerName,
          value: formDate
        }
      });
      setHasError(false);
    }
  };
  const handleIsActive = value => {
    if (value === "ACT") {
      return true;
    } else {
      return false;
    }
  };

  const handleOnChangeIsActive = value => {
    if (value) {
      return SmopStatusEnum.ACT;
    } else {
      return SmopStatusEnum.INA;
    }
  };
  const handleBusinessDetails = value => {
    const checker = value === "true" || value === true ? true : false;
    if (!checker) {
      formData.bankName = "";
      formData.acctName = "";
      formData.acctNum = "";
      formData.financeName = "";
      formData.financeMobileNum = "";
      formData.financeEmailAddress = "";
    } else {
      formData.bankName = formData.bankDetails.bankName;
      formData.acctName = formData.bankDetails.bankAccountName;
      formData.acctNum = formData.bankDetails.bankAccountNo;

      formData.financeName = formData.bankDetails.financeName;
      formData.financeMobileNum = formData.bankDetails.financeMobileNo;
      formData.financeEmailAddress = formData.bankDetails.financeEmail;
    }
  };
  const handleIsOtherBusiness = value => {
    if (value === "true" || value === true) {
      return true;
    } else if (value === "false" || value === false) {
      return false;
    }
  };

  const InStorePickupDeliveryTypeCheckbox = () => {
    if (!formData.isInMallPickup) {
      return null;
    }
    if (mallDisplayValue === "SM Fulfilled") {
      return null;
    }

    const canManageInStoreCancelConfig = hasPermission(
      PermissionEnum.MANAGE_IN_STORE_CANCEL_CONFIG,
      user
    );
    return (
      <>
        <div
          style={{
            alignItems: "start",
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  id="isInStorePickupCheckBox"
                  color="primary"
                  name="isInStorePickup"
                  checked={formData.isInStorePickup}
                  onChange={e => {
                    change({
                      target: {
                        name: e.target.name,
                        value: e.target.checked
                      }
                    });
                  }}
                />
              }
              label="In-store pickup"
              labelPlacement="end"
            />
            {canManageInStoreCancelConfig &&
              (autoCancelConfig?.isEnabled ?? true) && (
                <p
                  style={{
                    color: "gray",
                    fontSize: "12px",
                    margin: "0 0 0 35px"
                  }}
                >
                  Orders will be auto-cancelled after{" "}
                  {autoCancelConfig?.cancellationDays ??
                    DEFAULT_AUTO_CANCEL_DAYS}{" "}
                  {autoCancelConfig?.cancellationDays === 1 ? "day" : "days"}
                </p>
              )}
          </div>
          <IconButton
            onClick={() => setIsAutoCancelConfigModalOpen(true)}
            disabled={!canManageInStoreCancelConfig}
          >
            <SettingsIcon
              style={{
                color: canManageInStoreCancelConfig ? "#0030FF" : "gray"
              }}
            />
          </IconButton>
        </div>
      </>
    );
  };

  const StandardDeliveryTypeCheckbox = () => {
    if (!formData.isMallStandardDeliveryEnabled) {
      return null;
    }
    if (mallDisplayValue === "SM Fulfilled") {
      return null;
    }

    return (
      <div
        style={{
          alignItems: "start",
          display: "flex",
          justifyContent: "space-between"
        }}
      >
        <div>
          <FormControlLabel
            control={
              <Checkbox
                id="isStandardDeliveryCheckbox"
                color="primary"
                name="isStandardDeliveryEnabled"
                checked={formData.isStandardDeliveryEnabled}
                onChange={e => {
                  change({
                    target: {
                      name: e.target.name,
                      value: e.target.checked
                    }
                  });
                }}
              />
            }
            label="Standard Delivery"
            labelPlacement="end"
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <FormSpacer />
      <CardContent>
        <Grids>
          <div>
            <CardSpacer />
            <Card>
              <CardTitle
                title={intl.formatMessage({
                  defaultMessage: "Store Information",
                  description: "dialog header"
                })}
                toolbar={
                  <>
                    {isAcquisitionOmiAdmin && (
                      <FormControlLabel
                        control={
                          <StoreFormFeatureCheckbox
                            id="isFeaturedCheckbox"
                            icon={<StarIcon />}
                            checkedIcon={<StarIcon />}
                            name="isFeatured"
                            color="primary"
                            checked={formData.isFeatured}
                            onChange={e => {
                              change({
                                target: {
                                  name: e.target.name,
                                  value: e.target.checked
                                }
                              });
                            }}
                            disabled={disable || !hasManageTenantPermission}
                          />
                        }
                        label="Feature Shop"
                        labelPlacement="end"
                      />
                    )}
                  </>
                }
              />
              <CardContent
                className={
                  hasManageTenantPermission ? "" : classes.cardContentDisabled
                }
              >
                <SingleAutocompleteSelectField
                  idInputTxt="sellerBrandTxt"
                  displayValue={sellerDisplayValue}
                  label={intl.formatMessage({
                    defaultMessage: "Seller (Brand)*"
                  })}
                  name="sellerBrand"
                  choices={brands}
                  disabled={disable || isSellerMasterOrStoreAdmin}
                  value={formData.sellerBrand || ""}
                  onChange={event => {
                    onBrandChange(event);
                    onBrandChangeStoreType(event);
                  }}
                  error={!!error.sellerBrand}
                  helperText={error.sellerBrand}
                  InputProps={{
                    autoComplete: "off"
                  }}
                  className={
                    isSellerMasterOrStoreAdmin ? classes.disabledField : ""
                  }
                  fetchChoices={fetchBrands}
                  {...fetchMoreBrands}
                />
                {isFnB &&
                (!isSellerMasterOrStoreAdmin || formData.isUseTenantCluster) ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="isUseTenantClusterCheckBox"
                        color="primary"
                        name="isUseTenantCluster"
                        checked={formData.isUseTenantCluster}
                        onChange={e => {
                          if (e.target.checked) {
                            onClusterChange({
                              target: {
                                name: "cluster",
                                value: ""
                              }
                            });
                          }
                          change({
                            target: {
                              name: e.target.name,
                              value: e.target.checked
                            }
                          });
                        }}
                        disabled={disable || isSellerMasterOrStoreAdmin}
                      />
                    }
                    label="Define different Cluster of Store"
                    labelPlacement="end"
                  />
                ) : (
                  ""
                )}
                <FormSpacer />
                {isFnB && formData.isUseTenantCluster && (
                  <SingleAutocompleteSelectField
                    label={intl.formatMessage({
                      defaultMessage: "Cluster"
                    })}
                    name="cluster"
                    displayValue={
                      !formData.isUseTenantCluster ? "" : clusterDisplayValue
                    }
                    choices={clusters}
                    error={!!error.cluster}
                    helperText={error.cluster}
                    disabled={
                      !isFnB ||
                      !formData.isUseTenantCluster ||
                      isSellerMasterOrStoreAdmin
                    }
                    value={formData.isUseTenantCluster ? formData.cluster : ""}
                    onChange={onClusterChange}
                    fetchChoices={fetchClusters}
                    {...fetchMoreClusters}
                  />
                )}
                <FormSpacer />
                <div className={classes.gridContainer}>
                  <SingleAutocompleteSelectField
                    idInputTxt="mallBranchTxt"
                    label={intl.formatMessage({
                      defaultMessage: "Mall*"
                    })}
                    disabled={disable || isSellerMasterOrStoreAdmin}
                    displayValue={mallDisplayValue}
                    choices={malls}
                    name="mallBranch"
                    onChange={onMallChange}
                    value={formData.mallBranch || ""}
                    error={!!error.mallBranch}
                    helperText={error.mallBranch}
                    fetchChoices={fetchMalls}
                    {...fetchMoreMalls}
                    InputProps={{
                      autoComplete: "off"
                    }}
                    className={classNames(classes.textfieldPadding, {
                      [classes.disabledField]: isSellerMasterOrStoreAdmin
                    })}
                  />
                  <SingleAutocompleteSelectField
                    idInputTxt="storeClassificationTxt"
                    label={intl.formatMessage({
                      defaultMessage: "Store Classification"
                    })}
                    disabled={disable || isSellerMasterOrStoreAdmin || !isFnB}
                    onChange={onSubTenantTypeChange}
                    choices={subTenantChoices}
                    name="subTenantType"
                    value={isFnB ? formData.subTenantType || "" : ""}
                    displayValue={isFnB ? subTenantTypeDisplay : ""}
                    error={!!error.subTenantType}
                    helperText={error.subTenantType}
                    InputProps={{
                      autoComplete: "off"
                    }}
                    className={
                      isSellerMasterOrStoreAdmin ? classes.disabledField : ""
                    }
                  />
                </div>
                <FormSpacer />
                <TextField
                  id="storeLocationTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Store location"
                  })}
                  name="location"
                  onChange={change}
                  value={formData.location || ""}
                  error={!!error.location}
                  helperText={error.location}
                  disabled={disable || isSellerMasterOrStoreAdmin}
                  InputProps={{
                    autoComplete: "off"
                  }}
                  className={classNames(classes.textfieldPadding, {
                    [classes.disabledField]: isSellerMasterOrStoreAdmin
                  })}
                />
                <FormSpacer />
                <TextField
                  id="contractIDTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Contract ID*"
                  })}
                  name="contractId"
                  onChange={change}
                  value={formData.contractId || ""}
                  error={!!error.contractId}
                  helperText={error.contractId}
                  disabled={disable || isSellerMasterOrStoreAdmin}
                  InputProps={{
                    autoComplete: "off"
                  }}
                  className={
                    isSellerMasterOrStoreAdmin ? classes.disabledField : ""
                  }
                />
                <FormSpacer />
                <SingleAutocompleteSelectField
                  idInputTxt="paymentFeeRateTxt"
                  displayValue={formData.paymentFeeRate || ""}
                  label={intl.formatMessage({
                    defaultMessage: "Payment Fee Rate*"
                  })}
                  disabled={disable || isSellerMasterOrStoreAdmin}
                  name="paymentFeeRate"
                  onChange={onPaymentChange}
                  choices={paymentRateChoices}
                  value={formData.paymentFeeRate || ""}
                  error={!!error.paymentFeeRate}
                  helperText={error.paymentFeeRate}
                  InputProps={{
                    autoComplete: "off"
                  }}
                  className={
                    isSellerMasterOrStoreAdmin ? classes.disabledField : ""
                  }
                />
                <FormSpacer />
                <SingleAutocompleteSelectField
                  idInputTxt="rentalTypeTxt"
                  label={intl.formatMessage({
                    defaultMessage: "Rental Type"
                  })}
                  disabled={disable || isSellerMasterOrStoreAdmin}
                  onChange={onRentalChange}
                  choices={rentTypeChoices}
                  name="rentalType"
                  value={formData.rentalType || ""}
                  displayValue={rentTypeDisplay}
                  error={!!error.rentalType}
                  helperText={error.rentalType}
                  InputProps={{
                    autoComplete: "off"
                  }}
                  className={
                    isSellerMasterOrStoreAdmin ? classes.disabledField : ""
                  }
                />
                <FormSpacer />
                <TextField
                  id="subMIDTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Sub MID"
                  })}
                  name="ipay88SubMid"
                  onChange={change}
                  value={formData.ipay88SubMid || ""}
                  error={!!error.ipay88SubMid}
                  helperText={error.ipay88SubMid}
                  disabled={disable || isSellerMasterOrStoreAdmin}
                  InputProps={{
                    autoComplete: "off"
                  }}
                  className={
                    isSellerMasterOrStoreAdmin ? classes.disabledField : ""
                  }
                />
                <FormSpacer />
                <TextField
                  id="tinNoTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "TIN"
                  })}
                  name="tinNo"
                  onChange={change}
                  value={formData.tinNo || ""}
                  onKeyPress={event => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  InputProps={{
                    autoComplete: "off",
                    readOnly: isSellerMasterOrStoreAdmin
                  }}
                  disabled={disable || isSellerMasterOrStoreAdmin}
                  className={
                    isSellerMasterOrStoreAdmin ? classes.disabledField : ""
                  }
                />
              </CardContent>
            </Card>
            <CardSpacer />
            {!mediumUpwards && (
              <>
                <StoreBannerImage
                  data={formData}
                  onChange={change}
                  onImageUpload={file => onImageUpload(file)}
                  hasManageTenantPermission={hasManageTenantPermission}
                />
                <FormSpacer />
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Store Availability",
                      description: "store availability"
                    })}
                  />
                  <CardContent>
                    <Grid container direction="row" spacing={2}>
                      {formData.isActivate === "ACT" ? (
                        <Grid item xs={12} style={{ textAlign: "start" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                id="activeToggle"
                                color="primary"
                                name="isActivate"
                                checked={handleIsActive(formData.isActivate)}
                                onChange={e => {
                                  if (!e.target.checked) {
                                    setModalStatus(true);
                                  } else {
                                    change({
                                      target: {
                                        name: e.target.name,
                                        value: handleOnChangeIsActive(
                                          e.target.checked
                                        )
                                      }
                                    });
                                  }
                                }}
                                disabled={disable || isStoreAdmin}
                              />
                            }
                            label="Active"
                            labelPlacement="end"
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12} style={{ textAlign: "start" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                id="activeToggle"
                                color="primary"
                                name="isActivate"
                                checked={handleIsActive(formData.isActivate)}
                                onChange={e => {
                                  if (!e.target.checked) {
                                    setModalStatus(true);
                                  } else {
                                    change({
                                      target: {
                                        name: e.target.name,
                                        value: handleOnChangeIsActive(
                                          e.target.checked
                                        )
                                      }
                                    });
                                  }
                                }}
                                disabled={disable || isStoreAdmin}
                              />
                            }
                            label="Inactive"
                            labelPlacement="end"
                          />
                        </Grid>
                      )}
                      <Grid item xs={6} sm={6}>
                        <TextField
                          id="openingTimeTxt"
                          fullWidth
                          label={intl.formatMessage({
                            defaultMessage: "Opening Time"
                          })}
                          name="operationStartTime"
                          type="time"
                          onChange={change}
                          value={formData.operationStartTime || ""}
                          InputProps={{
                            autoComplete: "off"
                          }}
                          InputLabelProps={{
                            shrink: true
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <TextField
                          id="closingTimeTxt"
                          fullWidth
                          label={intl.formatMessage({
                            defaultMessage: "Closing Time"
                          })}
                          name="operationEndTime"
                          type="time"
                          onChange={change}
                          value={formData.operationEndTime || ""}
                          InputProps={{
                            autoComplete: "off"
                          }}
                          InputLabelProps={{
                            shrink: true
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                <FormSpacer />
                <>
                  <Card>
                    <CardTitle
                      title={intl.formatMessage({
                        defaultMessage: "Delivery Type",
                        description: "delivery type"
                      })}
                    />
                    <CardContent>
                      {formData.storeType === "FOOD_AND_BEVERAGES" ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="isExpressLongDistanceDeliveryAllowedCheckBox"
                              color="primary"
                              name="isExpressLongDistanceDeliveryAllowed"
                              checked={
                                formData.isExpressLongDistanceDeliveryAllowed
                              }
                              onChange={e => {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: e.target.checked
                                  }
                                });
                              }}
                            />
                          }
                          label="Allow Express - Long Distance Delivery"
                          labelPlacement="end"
                        />
                      ) : (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="isSameDayDeliveryAllowedCheckBox"
                                color="primary"
                                name="isSameDayDeliveryAllowed"
                                checked={formData.isSameDayDeliveryAllowed}
                                onChange={e => {
                                  change({
                                    target: {
                                      name: e.target.name,
                                      value: e.target.checked
                                    }
                                  });
                                }}
                              />
                            }
                            label="Same Day Delivery"
                            labelPlacement="end"
                          />
                          <StandardDeliveryTypeCheckbox />
                          <InStorePickupDeliveryTypeCheckbox />
                        </>
                      )}
                    </CardContent>
                  </Card>
                  <FormSpacer />
                </>
              </>
            )}
            <FormSpacer />
            <Divider variant="fullWidth" />
            <Card>
              <FormSpacer />
              <CardTitle
                title={intl.formatMessage({
                  defaultMessage: "Promotional Ribbon",
                  description: "Promotional Ribbon"
                })}
              />
              <CardContent
                className={
                  hasManageTenantPermission ? "" : classes.cardContentDisabled
                }
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      id="descriptionTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Description"
                      })}
                      name="promoRibbonDescription"
                      value={formData.promoRibbonDescription || ""}
                      onChange={e => {
                        change({
                          target: {
                            name: e.target.name,
                            value: e.target.value
                          }
                        });
                      }}
                      inputProps={{
                        maxLength: 30
                      }}
                      disabled={!isSellerMasterOrStoreAdmin}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <TextField
                      id="startDateTxt"
                      fullWidth
                      label="Start Date"
                      name="promoRibbonStart"
                      type="datetime-local"
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={formData.promoRibbonStart}
                      onChange={value =>
                        handleTime(value, "promoRibbonStart", change)
                      }
                      onKeyPress={event => {
                        if (event.key) {
                          event.preventDefault();
                          event.stopPropagation();
                        }
                      }}
                      inputProps={{
                        max: formData.promoRibbonEnd,
                        min: Moment(new Date())
                          .local()
                          .format("YYYY-MM-DDTHH:mm")
                      }}
                      disabled={!isSellerMasterOrStoreAdmin}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <TextField
                      id="endDateTxt"
                      fullWidth
                      label="End Date"
                      name="promoRibbonEnd"
                      type="datetime-local"
                      value={formData.promoRibbonEnd || ""}
                      onChange={value =>
                        handleTime(value, "promoRibbonEnd", change)
                      }
                      InputLabelProps={{
                        shrink: true
                      }}
                      onKeyPress={event => {
                        if (event.key) {
                          event.preventDefault();
                          event.stopPropagation();
                        }
                      }}
                      inputProps={{
                        min: formData.promoRibbonStart
                      }}
                      disabled={
                        !isSellerMasterOrStoreAdmin ||
                        formData.promoRibbonStart === "Invalid date" ||
                        formData.promoRibbonStart === ""
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <FormSpacer />
            {!mediumUpwards && (
              <Card>
                <CardTitle
                  title={intl.formatMessage({
                    defaultMessage: "Authorized Signatory",
                    description: "dialog header"
                  })}
                />
                <CardContent
                  className={
                    hasManageTenantPermission ? "" : classes.cardContentDisabled
                  }
                >
                  <TextField
                    id="signatoryNameTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Signatory Name"
                    })}
                    name="signatoryName"
                    value={formData.signatoryName || ""}
                    onChange={change}
                    error={!!error.signatoryName}
                    helperText={error.signatoryName}
                    disabled={disable || isSellerMasterOrStoreAdmin}
                  />
                  <FormSpacer />
                  <TextField
                    id="emailAddressTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Email Address"
                    })}
                    name="authorizeEmailAddress"
                    value={formData.authorizeEmailAddress || ""}
                    onChange={change}
                    error={!!error.authorizeEmailAddress}
                    helperText={error.authorizeEmailAddress}
                    disabled={disable || isSellerMasterOrStoreAdmin}
                  />
                  <FormSpacer />
                  <TextField
                    id="designationTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Designation"
                    })}
                    name="designation"
                    value={formData.designation || ""}
                    onChange={change}
                    error={!!error.designation}
                    helperText={error.designation}
                    disabled={disable || isSellerMasterOrStoreAdmin}
                  />
                  <FormSpacer />
                  <TextField
                    id="mobileNoTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Mobile No."
                    })}
                    name="authorizeMobileNum"
                    value={formData.authorizeMobileNum || ""}
                    onChange={change}
                    error={!!error.authorizeMobileNum}
                    helperText={error.authorizeMobileNum}
                    disabled={disable || isSellerMasterOrStoreAdmin}
                  />
                </CardContent>
              </Card>
            )}
            {!mediumUpwards && (
              <Card>
                <CardTitle
                  title={intl.formatMessage({
                    defaultMessage: "Authorized Signatory",
                    description: "dialog header"
                  })}
                />
                <CardContent
                  className={
                    hasManageTenantPermission ? "" : classes.cardContentDisabled
                  }
                >
                  <Grids variant="uniform">
                    <TextField
                      id="signatoryNameTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Signatory Name"
                      })}
                      name="signatoryName"
                      value={formData.signatoryName || ""}
                      onChange={change}
                      error={!!error.signatoryName}
                      helperText={error.signatoryName}
                      disabled={disable || isSellerMasterOrStoreAdmin}
                    />
                    <TextField
                      id="emailAddressTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Email Address"
                      })}
                      name="authorizeEmailAddress"
                      value={formData.authorizeEmailAddress || ""}
                      onChange={change}
                      error={!!error.authorizeEmailAddress}
                      helperText={error.authorizeEmailAddress}
                      disabled={disable || isSellerMasterOrStoreAdmin}
                    />
                    <TextField
                      id="designationTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Designation"
                      })}
                      name="designation"
                      value={formData.designation || ""}
                      onChange={change}
                      error={!!error.designation}
                      helperText={error.designation}
                      disabled={disable || isSellerMasterOrStoreAdmin}
                    />
                    <TextField
                      id="mobileNoTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Mobile No."
                      })}
                      name="authorizeMobileNum"
                      value={formData.authorizeMobileNum || ""}
                      onChange={change}
                      error={!!error.authorizeMobileNum}
                      helperText={error.authorizeMobileNum}
                      disabled={disable || isSellerMasterOrStoreAdmin}
                    />
                  </Grids>
                </CardContent>
              </Card>
            )}
          </div>
          <div>
            {mediumUpwards && (
              <>
                <CardSpacer />
                <StoreBannerImage
                  data={formData}
                  onChange={change}
                  onImageUpload={file => onImageUpload(file)}
                  hasManageTenantPermission={hasManageTenantPermission}
                />
                <FormSpacer />
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Store Availability",
                      description: "store availability"
                    })}
                  />
                  <CardContent>
                    <Grid container direction="row" spacing={2}>
                      {formData.isActivate === "ACT" ? (
                        <Grid item xs={12} style={{ textAlign: "start" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                id="activeToggle"
                                color="primary"
                                name="isActivate"
                                checked={handleIsActive(formData.isActivate)}
                                onChange={e => {
                                  if (!e.target.checked) {
                                    setModalStatus(true);
                                  } else {
                                    change({
                                      target: {
                                        name: e.target.name,
                                        value: handleOnChangeIsActive(
                                          e.target.checked
                                        )
                                      }
                                    });
                                  }
                                }}
                                disabled={disable || isStoreAdmin}
                              />
                            }
                            label="Active"
                            labelPlacement="end"
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12} style={{ textAlign: "start" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                id="activeToggle"
                                color="primary"
                                name="isActivate"
                                checked={handleIsActive(formData.isActivate)}
                                onChange={e => {
                                  if (!e.target.checked) {
                                    setModalStatus(true);
                                  } else {
                                    change({
                                      target: {
                                        name: e.target.name,
                                        value: handleOnChangeIsActive(
                                          e.target.checked
                                        )
                                      }
                                    });
                                  }
                                }}
                                disabled={disable || isStoreAdmin}
                              />
                            }
                            label="Inactive"
                            labelPlacement="end"
                          />
                        </Grid>
                      )}
                      <Grid item xs={6} sm={6}>
                        <TextField
                          id="openingTimeTxt"
                          fullWidth
                          label={intl.formatMessage({
                            defaultMessage: "Opening Time"
                          })}
                          name="operationStartTime"
                          type="time"
                          onChange={change}
                          value={formData.operationStartTime || ""}
                          InputProps={{
                            autoComplete: "off"
                          }}
                          InputLabelProps={{
                            shrink: true
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <TextField
                          id="closingTimeTxt"
                          fullWidth
                          label={intl.formatMessage({
                            defaultMessage: "Closing Time"
                          })}
                          name="operationEndTime"
                          type="time"
                          onChange={change}
                          value={formData.operationEndTime || ""}
                          InputProps={{
                            autoComplete: "off"
                          }}
                          InputLabelProps={{
                            shrink: true
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                <FormSpacer />
                <>
                  <Card>
                    <CardTitle
                      title={intl.formatMessage({
                        defaultMessage: "Delivery Type",
                        description: "delivery type"
                      })}
                    />
                    <CardContent>
                      {formData.storeType === "FOOD_AND_BEVERAGES" ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="isExpressLongDistanceDeliveryAllowedCheckBox"
                              color="primary"
                              name="isExpressLongDistanceDeliveryAllowed"
                              checked={
                                formData.isExpressLongDistanceDeliveryAllowed
                              }
                              onChange={e => {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: e.target.checked
                                  }
                                });
                              }}
                            />
                          }
                          label="Allow Express - Long Distance Delivery"
                          labelPlacement="end"
                        />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column"
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="isSameDayDeliveryAllowedCheckBox"
                                color="primary"
                                name="isSameDayDeliveryAllowed"
                                checked={formData.isSameDayDeliveryAllowed}
                                onChange={e => {
                                  change({
                                    target: {
                                      name: e.target.name,
                                      value: e.target.checked
                                    }
                                  });
                                }}
                              />
                            }
                            label="Same Day Delivery"
                            labelPlacement="end"
                          />
                          <StandardDeliveryTypeCheckbox />
                          <InStorePickupDeliveryTypeCheckbox />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <FormSpacer />
                </>
              </>
            )}
            <FormSpacer />
            {!isStoreAdmin && (
              <>
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Business Details",
                      description: "dialog header"
                    })}
                  />
                  <CardContent
                    className={
                      hasManageTenantPermission
                        ? ""
                        : classes.cardContentDisabled
                    }
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="useOtherCompanyDetailCheckbox"
                              color="primary"
                              name="isActivateCompanyDetails"
                              checked={handleIsOtherBusiness(
                                formData.isActivateCompanyDetails
                              )}
                              onClick={() =>
                                handleBusinessDetails(
                                  formData.isActivateCompanyDetails
                                )
                              }
                              onChange={e => {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: e.target.checked
                                  }
                                });
                              }}
                              disabled={disable || isSellerMasterOrStoreAdmin}
                            />
                          }
                          label="Use other Company Details"
                          labelPlacement="end"
                        />

                        <div
                          style={{
                            alignItems: "center",
                            display: "flex",
                            flexWrap: "wrap",
                            marginLeft: "2rem"
                          }}
                        >
                          <Grid item xs={1}>
                            <InfoIcon className={classes.informationIcon} />
                          </Grid>
                          <Grid item xs={11}>
                            <Typography variant="subtitle1">
                              Check if the Store will use different Company,
                              Bank and Finance against the Seller Master data
                            </Typography>
                          </Grid>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={12}></Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </Grids>
        <FormSpacer />
        <Card>
          <CardTitle
            title={intl.formatMessage({
              defaultMessage: "Accounting Details",
              description: "dialog header"
            })}
          />
          {!mediumUpwards && (
            <>
              <CardContent
                className={
                  hasManageTenantPermission ? "" : classes.cardContentDisabled
                }
              >
                <StoreFormInfoTextTypography variant="h6">
                  BANK DETAILS
                </StoreFormInfoTextTypography>
                <FormSpacer />
                <TextField
                  id="bankNameTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Bank Name"
                  })}
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={formData.bankName || ""}
                  name="bankName"
                  onChange={change}
                  error={!!error.bankName}
                  helperText={error.bankName}
                  disabled={
                    !handleIsOtherBusiness(formData.isActivateCompanyDetails) ||
                    isSellerMaster
                  }
                />
                <FormSpacer />
                <TextField
                  id="acctNameTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Acct. Name"
                  })}
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={formData.acctName || ""}
                  name="acctName"
                  onChange={change}
                  error={!!error.acctName}
                  helperText={error.acctName}
                  disabled={
                    !handleIsOtherBusiness(formData.isActivateCompanyDetails) ||
                    isSellerMaster
                  }
                />
                <FormSpacer />
                <TextField
                  id="acctNoTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Acct. No."
                  })}
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={formData.acctNum || ""}
                  name="acctNum"
                  onChange={change}
                  error={!!error.acctNum}
                  helperText={error.acctNum}
                  disabled={
                    !handleIsOtherBusiness(formData.isActivateCompanyDetails) ||
                    isSellerMaster
                  }
                />
                <FormSpacer />
                <StoreFormInfoTextTypography variant="h6">
                  FINANCE CONTACT DETAILS
                </StoreFormInfoTextTypography>
                <FormSpacer />
                <TextField
                  id="financeNameTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Name"
                  })}
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={formData.financeName || ""}
                  name="financeName"
                  onChange={change}
                  error={!!error.financeName}
                  helperText={error.financeName}
                  disabled={
                    !handleIsOtherBusiness(formData.isActivateCompanyDetails) ||
                    isSellerMasterOrStoreAdmin
                  }
                />
                <FormSpacer />
                <TextField
                  id="financeEmailAddressTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Email address"
                  })}
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={formData.financeEmailAddress || ""}
                  name="financeEmailAddress"
                  onChange={change}
                  error={!!error.financeEmailAddress}
                  helperText={error.financeEmailAddress}
                  disabled={
                    !handleIsOtherBusiness(formData.isActivateCompanyDetails) ||
                    isSellerMasterOrStoreAdmin
                  }
                />
                <FormSpacer />
                <TextField
                  id="financeMobileNoTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Mobile No."
                  })}
                  InputLabelProps={{
                    shrink: true
                  }}
                  value={formData.financeMobileNum || ""}
                  name="financeMobileNum"
                  onChange={change}
                  error={!!error.financeMobileNum}
                  helperText={error.financeMobileNum}
                  disabled={
                    !handleIsOtherBusiness(formData.isActivateCompanyDetails) ||
                    isSellerMasterOrStoreAdmin
                  }
                />
              </CardContent>
            </>
          )}
          {mediumUpwards && (
            <Grids variant="uniform">
              <div>
                <CardContent>
                  <StoreFormInfoTextTypography variant="h6">
                    BANK DETAILS
                  </StoreFormInfoTextTypography>
                  <FormSpacer />
                  <TextField
                    id="bankNameTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Bank Name"
                    })}
                    InputLabelProps={{
                      shrink: true
                    }}
                    value={formData.bankName || ""}
                    name="bankName"
                    onChange={change}
                    error={!!error.bankName}
                    helperText={error.bankName}
                    disabled={
                      !handleIsOtherBusiness(
                        formData.isActivateCompanyDetails
                      ) || isSellerMaster
                    }
                  />
                  <FormSpacer />
                  <TextField
                    id="acctNameTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Acct. Name"
                    })}
                    InputLabelProps={{
                      shrink: true
                    }}
                    value={formData.acctName || ""}
                    name="acctName"
                    onChange={change}
                    error={!!error.acctName}
                    helperText={error.acctName}
                    disabled={
                      !handleIsOtherBusiness(
                        formData.isActivateCompanyDetails
                      ) || isSellerMaster
                    }
                  />
                  <FormSpacer />
                  <TextField
                    id="acctNoTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Acct. No."
                    })}
                    InputLabelProps={{
                      shrink: true
                    }}
                    value={formData.acctNum || ""}
                    name="acctNum"
                    onChange={change}
                    error={!!error.acctNum}
                    helperText={error.acctNum}
                    disabled={
                      !handleIsOtherBusiness(
                        formData.isActivateCompanyDetails
                      ) || isSellerMaster
                    }
                  />
                </CardContent>
              </div>
              <div>
                <CardContent>
                  <>
                    <StoreFormInfoTextTypography variant="h6">
                      FINANCE CONTACT DETAILS
                    </StoreFormInfoTextTypography>
                    <FormSpacer />
                    <TextField
                      id="financeNameTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Name"
                      })}
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={formData.financeName || ""}
                      name="financeName"
                      onChange={change}
                      error={!!error.financeName}
                      helperText={error.financeName}
                      disabled={
                        !handleIsOtherBusiness(
                          formData.isActivateCompanyDetails
                        ) || isSellerMasterOrStoreAdmin
                      }
                    />
                    <FormSpacer />
                    <TextField
                      id="financeEmailAddressTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Email address"
                      })}
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={formData.financeEmailAddress || ""}
                      name="financeEmailAddress"
                      onChange={change}
                      error={!!error.financeEmailAddress}
                      helperText={error.financeEmailAddress}
                      disabled={
                        !handleIsOtherBusiness(
                          formData.isActivateCompanyDetails
                        ) || isSellerMasterOrStoreAdmin
                      }
                    />
                    <FormSpacer />
                    <TextField
                      id="financeMobileNumTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Mobile No."
                      })}
                      InputLabelProps={{
                        shrink: true
                      }}
                      value={formData.financeMobileNum || ""}
                      name="financeMobileNum"
                      onChange={change}
                      error={!!error.financeMobileNum}
                      helperText={error.financeMobileNum}
                      disabled={
                        !handleIsOtherBusiness(
                          formData.isActivateCompanyDetails
                        ) || isSellerMasterOrStoreAdmin
                      }
                    />
                  </>
                </CardContent>
              </div>
            </Grids>
          )}
        </Card>
        <SaveButtonBar
          onDelete={
            isSellerMaster
              ? null
              : formData.isActivate === "DEL"
              ? undefined
              : onDelete
          }
          disableDelete={!hasManageTenantPermission}
          onSave={submit}
          disabled={!hasChange}
          state={saveButtonBarState}
          onCancel={discardButton}
        />
        <ActionDialog
          confirmButtonState={"default"}
          onClose={() => {
            setModalStatus(false);
          }}
          onConfirm={() => {
            change({
              target: {
                name: "isActivate",
                value: handleOnChangeIsActive(false)
              }
            });
            setModalStatus(false);
          }}
          open={statusModal}
          title={intl.formatMessage({
            defaultMessage: "Deactivate Store ",
            description: "dialog title"
          })}
          variant="default"
        >
          <DialogContentText>
            <FormattedMessage defaultMessage="Are you sure to deactive this store?" />
          </DialogContentText>
        </ActionDialog>
      </CardContent>
    </>
  );
};

export default StoreEditForm;
